import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { reduxForm } from 'redux-form';
import get from 'lodash/get';
import truncate from 'lodash/truncate';

import {
  PrimaryModal, PrimaryButton, Status, ModalBranding, Popover, Divider, iconTypes,
} from '../../components/pbg-ui-library';
import { TIME_FORMAT_HOUR, timeFormats } from '../../../components/Time';
import { format as formatMoney } from '../../../lib/money'
import { formatMessage } from '../../../lib/intl';

import validate from './validate';
import { i18n } from './single-payer-payment.i18n';
import SinglePayerPaymentForm, { SinglePayerPaymentFormName } from './single-payer-payment-form';
import ContactInformation from './contact-information';

import '../../css/single-payer-payment.css';

class SinglePayerPayment extends React.PureComponent {
  formatMessage = formatMessage(this.props.intl ? this.props.intl : {});

  state = {
    isSignin: false,
  }

  static propTypes = {
    purchase: PropTypes.object.isRequired,
    onSubmitPayment: PropTypes.func.isRequired,
    onLogin: PropTypes.func.isRequired,
    connectWithFacebook: PropTypes.func.isRequired,
    user: PropTypes.object,
    alerts: PropTypes.arrayOf(PropTypes.object),
  };

  static defaultProps = {
    user: null,
    alerts: [],
  };

  onSubmit = (values) => {
    return this.props.onSubmitPayment(values);
  }

  onLogin = (values) => {
    return this.props.onLogin(values);
  }

  onLogout = () => {
    this.setState({ isSignin: false });
    return this.props.onLogout();
  }

  onSignIn = () => {
    this.props.change('__isSignIn', true);
    this.setState({ isSignin: true });
  }

  onCreateAccount = () => {
    this.props.change('__isSignIn', false);
    this.setState({ isSignin: false });
  }

  get isSignin() {
    const { user } = this.props;
    const { isSignin } = this.state;
    return !user && isSignin;
  }

  get deadlineDate() {
    const { purchase: { nextPaymentDueDeadline, nextPaymentDueDeadlineTimezone } } = this.props;
    return timeFormats.default(nextPaymentDueDeadline, nextPaymentDueDeadlineTimezone);
  }

  get deadlineTime() {
    const { purchase: { nextPaymentDueDeadline, nextPaymentDueDeadlineTimezone } } = this.props;
    return timeFormats[TIME_FORMAT_HOUR](nextPaymentDueDeadline, nextPaymentDueDeadlineTimezone);
  }

  get purchaseTotalAmount() {
    return formatMoney(get(this.props, 'purchase.currentTotalCostAmount'));
  }

  get merchantLogo() { return get(this.props, 'purchase.merchant.logoUrl'); }

  renderForm() {
    const { accountType, initialValues, onCardTokenized, onCardError, user } = this.props;

    return (
      <SinglePayerPaymentForm
        user={user}
        accountType={accountType}
        initialValues={initialValues}
        onCardTokenized={onCardTokenized}
        onCardError={onCardError}
        onLogout={this.onLogout}
        onSignIn={this.onSignIn}
        onSendLink={this.props.onSendLink}
        onCreateAccount={this.onCreateAccount}
        connectWithFacebook={this.props.connectWithFacebook}
      />
    );
  }

  renderFooter() {
    const { purchase: { merchant: { displayName } }, submitDisabled } = this.props;
    const userAgreementLink = 'http://www.paybygroup.com/user-agreement';
    return (
      <div className="single-payer-payment-footer clearfix">
        <p className="multi-currency-disclaimer pbg-desktop-small-text pbg-desktop-secondary-text">
          {this.formatMessage(i18n.footerDisclaimerBeginning)}
          <a className="pbg-consumer-desktop pbg-desktop-small-link" href={userAgreementLink} target="_blank">
            {this.formatMessage(i18n.userAgreement)}
          </a>
          {this.formatMessage(i18n.footerDisclaimerEnd, { merchant: displayName })}
        </p>
        <div className="single-payer-payment-footer-cta">
          <PrimaryButton disabled={submitDisabled}>
            {this.formatMessage(i18n.singlePayerPaymentSubmit, { amount: this.purchaseTotalAmount })}
          </PrimaryButton>
        </div>
      </div>
    );
  }

  renderSidebar() {
    return (
      <div className="single-payer-payment-sidebar">
        {this.renderPurchaseNameAndDescription()}
        {this.renderPurchaseStatus()}
        {this.renderMultiCurrencyDisclaimer()}
        {this.renderExternalLink()}
        {this.renderModalBranding()}
      </div>
    );
  }

  renderPurchaseName() {
    const { purchase: { product: { name } } } = this.props;
    return <h3 className="pbg-consumer-desktop pbg-desktop-label-strong">{name}</h3>
  }

  renderPurchaseDescriptionPopover() {
    const { purchase: { product: { description } } } = this.props;
    if (!description || description.length < 140) return null;
    return (
      <Popover
        trigger={props => (
          <a className="pbg-consumer-desktop pbg-desktop-label-link" href="" onClick={props.onClick}>
            {this.formatMessage(i18n.viewAllPurchaseDescription)}
          </a>
        )}
        content={<div>{this.renderPurchaseName()}<p>{description}</p></div>}
      />
    );
  }

  renderPurchaseDescription() {
    const { purchase: { product: { description } } } = this.props;
    return (
      <p>
        {truncate(description, { length: 140 }).split('\n').map((item, key) => {
          return <React.Fragment key={key}>{item}{ key ? <br/> : ''}</React.Fragment>;
        })}
        {this.renderPurchaseDescriptionPopover()}
      </p>
    )
  }

  renderPurchaseNameAndDescription() {
    return (
      <div className="purchase-name-and-description">
        {this.renderPurchaseName()}
        {this.renderPurchaseDescription()}
      </div>
    )
  }

  renderPurchaseStatus() {
    const { purchase: { nextPaymentDueDeadline } } = this.props;
    return (
      <div className="purchase-status-information">
        <Status
          label={this.formatMessage(i18n.statusTotal)}
          value={this.purchaseTotalAmount}
          hint=""
          iconType={iconTypes.PRICE_TAG}
        />
        {
          nextPaymentDueDeadline && (
            <Status
              label={this.formatMessage(i18n.statusPaymentDueBy)}
              value={this.deadlineDate}
              hint={this.deadlineTime}
              iconType={iconTypes.CALENDAR}
            />
          )
        }
      </div>
    )
  }

  renderMultiCurrencyDisclaimer() {
    const { purchase: { currencyCode, currentPayment: { processingCurrency } } } = this.props;
    if (currencyCode === processingCurrency) return null;
    return (
      <p className="multi-currency-disclaimer pbg-desktop-small-text pbg-desktop-secondary-text">
        {this.formatMessage(i18n.multiCurrencyDisclaimer, { currencyCode: processingCurrency } )}
      </p>
    )
  }

  renderExternalLink() {
    const { purchase: { product: { link }} } = this.props;
    if (!link) return null;
    return (
      <div className="purchase-external-link">
        <a className="pbg-consumer-desktop pbg-desktop-small-link" href={link} target="_blank">
          {this.formatMessage(i18n.externalLink)}
        </a>
      </div>
    )
  }

  renderModalBranding() {
    return <ModalBranding logo={this.merchantLogo} />;
  }

  renderSigninModal() {
    const { alerts, handleSubmit } = this.props;
    return (
      <PrimaryModal
        form
        formProps={{ onSubmit: handleSubmit(this.onLogin) }}
        mainContent={this.renderLoginForm()}
        sidebarContent={this.renderSidebar()}
        alerts={alerts}
      />
    );
  }


  renderLoginForm() {
    const { user, accountType, onLogout } = this.props;

    return (
      <div className="single-payer-payment-form">
        <h3 className="form-title pbg-consumer-desktop pbg-desktop-heading-3">
          {this.formatMessage(i18n.enterDetails)}
        </h3>
        <Divider label={this.formatMessage(i18n.contactInformationTitle)} />
        <div className="contact-information">
          <ContactInformation
            user={user}
            accountType={accountType}
            onLogout={onLogout}
            onSignIn={this.onSignIn}
            onCreateAccount={this.onCreateAccount}
            onSendLink={this.props.onSendLink}
            connectWithFacebook={this.props.connectWithFacebook}
            signIn
          />
        </div>
      </div>
    );
  }

  renderPaymentModal() {
    const { alerts, handleSubmit } = this.props;
    return (
      <PrimaryModal
        form
        formProps={{ onSubmit: handleSubmit(this.onSubmit) }}
        mainContent={this.renderForm()}
        footerContent={this.renderFooter()}
        sidebarContent={this.renderSidebar()}
        alerts={alerts}
      />
    );
  }

  render() {
    return (
      <div className="single-payer-payment">
        { this.isSignin ? this.renderSigninModal() : this.renderPaymentModal() }
      </div>
    );
  }
}

export default injectIntl(reduxForm({
  validate,
  form: SinglePayerPaymentFormName,
})(SinglePayerPayment));



// WEBPACK FOOTER //
// ./src/desktop/pages/single-payer-payment/single-payer-payment.js